import { inject, Injectable, OnDestroy, signal } from '@angular/core';

import { Branding, ConfigurationState, ServiceTypeItem } from '../../shared/interfaces';
import { DsLoggingService } from '../../shared/services/ds-logging.service';
import { AbortError } from '../clock/abort-error';
import { SignageDataSource } from '../data/signage-data-source';
import { SignageConfigurationModel } from './models';
import { ConfigurationParserService } from './configuration-parser.service';
import { RefreshTier } from './enums';

// Fetches the channel plus best-effort extras — branding and service names are not worth failing a display over.
@Injectable()
export class ConfigService implements OnDestroy {
  private readonly parser = inject(ConfigurationParserService);
  private readonly source = inject(SignageDataSource);
  private readonly logging = inject(DsLoggingService);
  private readonly value = signal<SignageConfigurationModel | null>(null);
  // Bumped per load; a slow response from a superseded channel must never overwrite the current one.
  private generation = 0;
  private pending?: AbortController;
  public readonly configuration = this.value.asReadonly();

  public async load(raw?: ConfigurationState, signal?: AbortSignal): Promise<SignageConfigurationModel> {
    this.pending?.abort();
    const controller = new AbortController();
    this.pending = controller;
    const generation = ++this.generation;
    const abort = (): void => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });

    if (signal?.aborted) {
      controller.abort();
    }

    try {
      AbortError.throwIfAborted(controller.signal);

      if (raw === undefined && !this.source.loadConfiguration) {
        throw new Error('Host must supply signage configuration');
      }

      const configuration = raw === undefined ? await this.source.loadConfiguration(controller.signal) : raw;
      AbortError.throwIfAborted(controller.signal);
      const [branding, serviceNames] = await Promise.all([
        this.optional('branding', () => this.source.loadBranding(controller.signal), {} as Branding),
        this.optional('service-names', () => this.source.loadServiceNames(controller.signal),
          configuration.serviceNames || [] as ServiceTypeItem[])
      ]);
      AbortError.throwIfAborted(controller.signal);

      // A newer load started while this one was in flight; its result wins, so this one is dropped.
      if (generation !== this.generation) {
        throw new AbortError();
      }

      const parsed: SignageConfigurationModel = { ...this.parser.parse({
        ...configuration, serviceNames,
        iconLarge: branding.iconLarge || configuration.iconLarge
      }, this.logging), branding };
      this.value.set(parsed);

      return parsed;
    } finally {
      signal?.removeEventListener('abort', abort);

      if (this.pending === controller) {
        this.pending = undefined;
      }
    }
  }

  public ngOnDestroy(): void {
    ++this.generation;
    this.pending?.abort();
  }

  // A failed extra is reported and swapped for its fallback, never rethrown — the channel itself still loads.
  private async optional<T>(feed: string, fetch: () => Promise<T>, fallback: T): Promise<T> {
    try {
      return await fetch();
    } catch {
      this.logging.trackEvent('feed_failed', { feed, tier: RefreshTier.Config });

      return fallback;
    }
  }
}
